export type AnalyticsEventType =
    | 'listing_view'
    | 'listing_search'
    | 'category_view'
    | 'telegram_contact_click'
    | 'seller_profile_view'
    | 'share_click'
    | 'report_submitted'
    | 'sell_cta_click'
    // The Telegram group directory. Platform-level: none belongs to a marketplace.
    | 'telegram_directory_view'
    | 'telegram_directory_country'
    | 'telegram_group_open'
    | 'telegram_directory_owner_cta'
    | 'telegram_group_connect_click'
    // A click INTO the directory from elsewhere on Bazio; metadata.source names the
    // placement (homepage, marketplace_nav, footer, for_communities).
    | 'telegram_directory_entry_click'

type EventPayload = {
    type: AnalyticsEventType
    listingId?: string
    sellerId?: string
    categoryId?: string
    metadata?: Record<string, unknown>
}

/**
 * Fire-and-forget event beacon.
 *
 * Uses sendBeacon where available so the request survives the page being
 * unloaded by an outbound Telegram link, which is exactly the moment the
 * contact-click event matters most. Never blocks navigation and never
 * surfaces an error to the visitor.
 */
export const trackEvent = (payload: EventPayload): void => {
    if (typeof window === 'undefined') return

    try {
        const body = JSON.stringify({
            ...payload,
            referrer: document.referrer || null,
            path: window.location.pathname
        })

        if (typeof navigator.sendBeacon === 'function') {
            navigator.sendBeacon('/api/analytics/event', new Blob([body], { type: 'application/json' }))

            return
        }

        void fetch('/api/analytics/event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true
        }).catch(() => undefined)
    } catch {
        // Analytics must never break the page it measures.
    }
}

/**
 * Records ONE listing view and resolves to the listing's new total.
 *
 * Deliberately not a beacon: the caller needs the number back so the page can
 * show the visit it just made, and there is no deduplication anywhere in this
 * path — every page load counts, however many times the same person refreshes.
 * Resolves to null when the request fails, so the page keeps the total it was
 * rendered with rather than showing a wrong one.
 */
export const recordListingView = async (
    listingId: string,
    sellerId?: string
): Promise<number | null> => {
    if (typeof window === 'undefined') return null

    try {
        const response = await fetch('/api/analytics/event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                type: 'listing_view',
                listingId,
                sellerId,
                referrer: document.referrer || null,
                path: window.location.pathname
            }),
            cache: 'no-store'
        })

        if (!response.ok) return null

        const data = (await response.json()) as { viewCount?: unknown }

        return typeof data.viewCount === 'number' ? data.viewCount : null
    } catch {
        // Analytics must never break the page it measures.
        return null
    }
}

/** Events already sent in this page session, keyed by type and subject. */
const sent = new Set<string>()

/**
 * Sends an event AT MOST ONCE per subject for as long as the page stays
 * loaded. Use this for anything that increments a counter, so a re-render, a
 * remount, or React running an effect twice in development can never inflate
 * the number. A real second visit is a fresh page session and counts again.
 */
export const trackEventOnce = (payload: EventPayload): void => {
    if (typeof window === 'undefined') return

    const key = `${payload.type}:${payload.listingId ?? payload.sellerId ?? payload.categoryId ?? ''}`

    if (sent.has(key)) return

    sent.add(key)
    trackEvent(payload)
}
